import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../../../../assets/logoBase64';
import type { OrigenSalida, TipoSalida } from '../types';
import { origenLabels, tipoSalidaLabels } from '../types';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface DetalleLinea {
  origen: OrigenSalida;
  numeroDerc?: string | null;
  numeroGuiaProduccion: number;
  articulo?: string | null;
  presentacion?: string | null;
  calibre?: string | null;
  cantidad: number;
  kilos: number;
}

export interface GuiaSalidaPdfData {
  numero: string;
  fechaSalida: string;
  cliente?: string | null;
  tipoSalida: TipoSalida;
  placaVehiculo?: string | null;
  observaciones?: string | null;
  detalles: DetalleLinea[];
}

const formatNumber = (n: number, decimals = 2) =>
  n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateForFileName = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

export const guiaSalidaPdfService = {
  generatePdf(data: GuiaSalidaPdfData): void {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // ---------------------------------------------------------------------- HEADER
    const logoWidth = 25;
    const logoHeight = 17;
    doc.addImage(logoBase64, 'PNG', margin, yPos, logoWidth, logoHeight);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('GUIA DE SALIDA', pageWidth / 2, yPos + 8, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CPP. ISABEL GOMEZ DE VIZA', pageWidth - margin, yPos + 5, { align: 'right' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`N° ${data.numero}`, pageWidth - margin, yPos + 13, { align: 'right' });

    yPos += 24;

    // ---------------------------------------------------------------------- INFO
    doc.setFontSize(8);
    const col1 = margin + 3;
    const col2 = pageWidth / 2 + 10;
    const labelWidth = 30;
    const lineHeight = 5;

    const drawField = (label: string, value: string, x: number, y: number) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, x + labelWidth, y);
    };

    const infoStartY = yPos - 2;
    drawField('Fecha', formatDate(data.fechaSalida), col1, yPos);
    drawField('Tipo Salida', tipoSalidaLabels[data.tipoSalida] || data.tipoSalida, col2, yPos);
    yPos += lineHeight;

    drawField('Cliente', data.cliente || '-', col1, yPos);
    drawField('Placa Vehiculo', data.placaVehiculo || '-', col2, yPos);
    yPos += lineHeight;

    const infoEndY = yPos + 3;
    doc.setDrawColor(180);
    doc.rect(margin, infoStartY - 1, pageWidth - 2 * margin, infoEndY - infoStartY + 1);

    yPos = infoEndY + 5;

    // ---------------------------------------------------------------------- DETALLES
    const tableHeaders = [['Origen', 'DERC', 'N° Guia', 'Articulo', 'Presentacion', 'Calibre', 'Cant.', 'Kilos']];
    const tableBody: (string | number)[][] = data.detalles.map((d) => [
      origenLabels[d.origen] || d.origen,
      d.numeroDerc || '-',
      d.numeroGuiaProduccion,
      d.articulo || '-',
      d.presentacion || '-',
      d.calibre || '-',
      formatNumber(d.cantidad, 0),
      formatNumber(d.kilos),
    ]);

    autoTable(doc, {
      startY: yPos,
      head: tableHeaders,
      body: tableBody,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 1.5 },
      headStyles: {
        fillColor: [41, 65, 90],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 22 },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 25 },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 14, halign: 'right' },
        7: { cellWidth: 20, halign: 'right' },
      },
      theme: 'grid',
    });

    yPos = doc.lastAutoTable.finalY + 8;

    // ---------------------------------------------------------------------- TOTALES
    const totalKilos = data.detalles.reduce((s, d) => s + d.kilos, 0);
    const totalCantidad = data.detalles.reduce((s, d) => s + d.cantidad, 0);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `Total Items: ${data.detalles.length}    |    Total Cantidad: ${formatNumber(totalCantidad, 0)}    |    Total Kilos: ${formatNumber(totalKilos)} kg`,
      pageWidth - margin,
      yPos,
      { align: 'right' }
    );
    yPos += 10;

    // ---------------------------------------------------------------------- OBSERVACIONES
    if (data.observaciones && data.observaciones.trim().length > 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('OBSERVACIONES:', margin, yPos);
      yPos += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const obsLines = doc.splitTextToSize(data.observaciones, pageWidth - 2 * margin);
      doc.text(obsLines, margin, yPos);
      yPos += obsLines.length * 3.5 + 3;
    }

    yPos += 5;

    // ---------------------------------------------------------------------- FIRMAS
    const firmaWidth = 60;
    const firmaY = yPos + 15;
    const firma1X = margin + 20;
    const firma2X = pageWidth - margin - firmaWidth - 20;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');

    doc.line(firma1X, firmaY, firma1X + firmaWidth, firmaY);
    doc.text('JEFE DE PRODUCCION', firma1X + firmaWidth / 2, firmaY + 4, { align: 'center' });

    doc.line(firma2X, firmaY, firma2X + firmaWidth, firmaY);
    doc.text('CLIENTE / TRANSPORTISTA', firma2X + firmaWidth / 2, firmaY + 4, { align: 'center' });

    const fileName = `Guia_Salida_${data.numero}_${formatDateForFileName(data.fechaSalida)}.pdf`;
    doc.save(fileName);
  },
};
